import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertCircle, X, ExternalLink, Loader2, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { ModalShell } from '../ui';
import { attestPost, getAttestErrorMessage, getStoredWalletName, fetchFees, checkWalletBalance } from '../../services/ariadne';

/**
 * AttestModal — Gate modal shown BEFORE saving a post.
 * Attestation must succeed before the post is saved to the database.
 * 
 * Props:
 *   postData   — The post data to attest { title, subtitle, content }
 *   entity     — { id, type } — author or org publishing
 *   onSuccess  — Called with proof object when attestation succeeds
 *   onCancel   — Called when user cancels (back to editor, content intact)
 */
export default function AttestModal({ postData, entity, onSuccess, onCancel }) {
  const { t } = useTheme();
  const [status, setStatus] = useState('loading_fees'); // loading_fees | ready | loading | success | error | insufficient
  const [proof, setProof] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fees, setFees] = useState(null);
  const [balanceInfo, setBalanceInfo] = useState(null);

  const walletName = getStoredWalletName();

  // Fetch fees and check balance on mount
  useEffect(() => {
    async function init() {
      try {
        const feeData = await fetchFees();
        setFees(feeData);

        if (!walletName) {
          setErrorMsg('Wallet not connected. Please reconnect and try again.');
          setErrorCode('WALLET_NOT_CONNECTED');
          setStatus('error');
          return;
        }

        const balance = await checkWalletBalance(walletName);
        setBalanceInfo(balance);

        if (balance && !balance.sufficient) {
          setStatus('insufficient');
          return;
        }

        setStatus('ready');
      } catch (err) {
        console.error('Init error:', err);
        // Still allow attempt even if fee fetch fails
        setFees(null);
        setStatus('ready');
      }
    }
    init();
  }, []);

  // Auto-start attestation once ready
  useEffect(() => {
    if (status === 'ready') {
      const timer = setTimeout(() => handleAttest(), 600);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleAttest = async () => {
    setStatus('loading');
    setErrorMsg('');
    setErrorCode('');

    try {
      const result = await attestPost(walletName, postData, entity);
      setProof(result);
      setStatus('success');
    } catch (err) {
      setErrorMsg(getAttestErrorMessage(err));
      setErrorCode(err.code || 'UNKNOWN');
      setStatus('error');
    }
  };

  const handleDone = () => {
    if (proof && onSuccess) {
      onSuccess(proof);
    }
  };

  const copyDiagnostics = () => {
    const info = [
      `Error: ${errorCode}`,
      `Message: ${errorMsg}`,
      `Wallet: ${walletName || 'none'}`,
      `Network: mainnet`,
      `Balance: ${balanceInfo ? balanceInfo.balanceAda + ' ADA' : 'unknown'}`,
      `Fees: ${fees ? fees.total.ada + ' ADA' : 'unknown'}`,
      `Time: ${new Date().toISOString()}`,
      `Post title: ${postData?.title || 'untitled'}`,
      `Entity: ${entity?.type}/${entity?.id?.slice(0, 8)}...`
    ].join('\n');
    navigator.clipboard.writeText(info).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getErrorHelp = () => {
    switch (errorCode) {
      case 'USER_REJECTED':
        return 'You cancelled the transaction in your wallet. Click "Try Again" to restart, or "Back to Editor" to return without publishing.';
      case 'INSUFFICIENT_FUNDS':
        return `Your wallet needs at least ${fees ? fees.minimumWalletBalance.ada : 3} ADA. The attestation costs ${fees ? fees.total.ada : '~1.2'} ADA (${fees ? fees.adminFee.ada : 1} ADA service fee + ~${fees ? fees.networkFee.ada : 0.2} ADA network fee).`;
      case 'WALLET_NOT_CONNECTED':
        return 'Your wallet session may have expired. Go back to the editor, reconnect your wallet, then try publishing again.';
      case 'API_UNAVAILABLE':
        return 'The Ariadne attestation service is down. This is temporary — try again in a few minutes.';
      case 'TX_BUILD_FAILED':
        return 'The transaction could not be built. This usually means the wallet has no UTXOs or is on the wrong network.';
      case 'NETWORK_ERROR':
        return 'Could not reach the attestation service. Check your internet connection and try again.';
      default:
        return 'Something went wrong during attestation. You can try again or go back to the editor — your content is safe.';
    }
  };

  const explorerUrl = proof
    ? `https://cardanoscan.io/transaction/${proof.txHash}`
    : null;

  // Fee breakdown component
  const FeeBreakdown = ({ compact }) => {
    if (!fees) return null;
    
    if (compact) {
      return (
        <span style={{ fontSize: '13px', color: t.textSecondary }}>
          Cost: ~{fees.total.ada} ADA
        </span>
      );
    }

    return (
      <div style={{
        background: t.surface,
        borderRadius: '8px',
        padding: '12px 16px',
        border: `1px solid ${t.border || t.borderStrong}`,
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', color: t.textSecondary }}>Service fee</span>
          <span style={{ fontSize: '13px', color: t.text, fontFamily: 'monospace' }}>{fees.adminFee.ada} ADA</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', color: t.textSecondary }}>Network fee</span>
          <span style={{ fontSize: '13px', color: t.text, fontFamily: 'monospace' }}>~{fees.networkFee.ada} ADA</span>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          paddingTop: '8px', 
          borderTop: `1px solid ${t.border || t.borderStrong}` 
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: t.text }}>Total</span>
          <span style={{ fontSize: '13px', fontWeight: 600, color: t.text, fontFamily: 'monospace' }}>~{fees.total.ada} ADA</span>
        </div>
      </div>
    );
  };

  return (
    <ModalShell blur zIndex={9999} maxWidth="480px" panelStyle={{ backgroundColor: t.modalBg, boxShadow: '0 8px 16px rgba(0,0,0,0.1)', fontFamily: 'Poppins, sans-serif' }}>

        {/* LOADING FEES */}
        {status === 'loading_fees' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Loader2
              size={32}
              color={t.text}
              style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }}
            />
            <p style={{ fontSize: '14px', color: t.textSecondary }}>
              Preparing attestation...
            </p>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* INSUFFICIENT BALANCE */}
        {status === 'insufficient' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <AlertCircle size={28} color={t.error || '#dc2626'} />
              <h2 style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '22px',
                fontWeight: 600,
                color: t.text,
                margin: 0
              }}>
                Insufficient ADA
              </h2>
            </div>

            <p style={{ fontSize: '14px', color: t.text, lineHeight: '1.6', marginBottom: '16px' }}>
              Your wallet doesn't have enough ADA to cover the attestation cost.
            </p>

            {/* Balance vs Required */}
            <div style={{
              background: t.surface,
              borderRadius: '8px',
              padding: '12px 16px',
              border: `1px solid ${t.border || t.borderStrong}`,
              marginBottom: '16px'
            }}>
              {balanceInfo && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: t.textSecondary }}>Your balance</span>
                  <span style={{ fontSize: '13px', color: t.error || '#dc2626', fontFamily: 'monospace', fontWeight: 600 }}>
                    {balanceInfo.balanceAda.toFixed(2)} ADA
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: t.textSecondary }}>Minimum required</span>
                <span style={{ fontSize: '13px', color: t.text, fontFamily: 'monospace', fontWeight: 600 }}>
                  {fees ? fees.minimumWalletBalance.ada : 3} ADA
                </span>
              </div>
            </div>

            <FeeBreakdown />

            <p style={{ fontSize: '13px', color: t.textSecondary, lineHeight: '1.5', marginBottom: '20px' }}>
              Add ADA to your wallet and try again. Your content is safe in the editor.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={onCancel}
                style={{
                  background: t.buttonSecondaryBg || 'transparent',
                  color: t.buttonSecondaryText || t.text,
                  border: `1px solid ${t.buttonSecondaryBorder || t.borderStrong}`,
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer'
                }}
              >
                Back to Editor
              </button>
              <button
                onClick={() => { setStatus('ready'); }}
                style={{
                  background: t.buttonBg,
                  color: t.buttonText,
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '0.9';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Retry
              </button>
            </div>
          </>
        )}

        {/* READY — brief moment before auto-starting */}
        {status === 'ready' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Shield size={40} color={t.text} style={{ marginBottom: '16px' }} />
            <h2 style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '22px',
              fontWeight: 600,
              color: t.text,
              margin: '0 0 8px 0'
            }}>
              Preparing Attestation...
            </h2>
            <p style={{ fontSize: '13px', color: t.textSecondary, marginBottom: '12px' }}>
              Your wallet will ask you to approve a transaction.
            </p>
            {fees && <FeeBreakdown compact />}
          </div>
        )}

        {/* LOADING — waiting for wallet signature */}
        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Loader2
              size={40}
              color={t.text}
              style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }}
            />
            <h2 style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '22px',
              fontWeight: 600,
              color: t.text,
              margin: '0 0 8px 0'
            }}>
              Waiting for wallet...
            </h2>
            <p style={{ fontSize: '14px', color: t.text, lineHeight: '1.6', marginBottom: '4px' }}>
              Approve the transaction in your {walletName === 'lace' ? 'Lace' : 'Eternl'} extension.
            </p>
            {fees && (
              <p style={{ fontSize: '13px', color: t.textSecondary, marginBottom: '4px' }}>
                Total cost: ~{fees.total.ada} ADA ({fees.adminFee.ada} ADA fee + ~{fees.networkFee.ada} ADA network)
              </p>
            )}
            <p style={{ fontSize: '13px', color: t.textSecondary }}>
              This records a cryptographic fingerprint of your post on Cardano.
            </p>

            <button
              onClick={onCancel}
              style={{
                marginTop: '24px',
                background: 'transparent',
                color: t.textSecondary,
                border: 'none',
                fontSize: '13px',
                fontFamily: 'Poppins, sans-serif',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '4px 8px'
              }}
            >
              Cancel and return to editor
            </button>

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* SUCCESS */}
        {status === 'success' && proof && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <CheckCircle size={48} color={t.success || '#16a34a'} style={{ marginBottom: '12px' }} />
              <h2 style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '22px',
                fontWeight: 600,
                color: t.text,
                margin: '0 0 8px 0'
              }}>
                Post Attested
              </h2>
              <p style={{ fontSize: '14px', color: t.textSecondary }}>
                Your post now has a permanent proof of existence on Cardano.
              </p>
            </div>

            <div style={{
              background: t.surface,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              border: `1px solid ${t.border || t.borderStrong}`
            }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontWeight: 500, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Transaction Hash
                </span>
                <p style={{
                  fontSize: '13px',
                  color: t.text,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  margin: '4px 0 0 0'
                }}>
                  {proof.txHash}
                </p>
              </div>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 500, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Content Hash (SHA-256)
                </span>
                <p style={{
                  fontSize: '13px',
                  color: t.text,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  margin: '4px 0 0 0'
                }}>
                  {proof.contentHash}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {explorerUrl && (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: t.buttonSecondaryBg || 'transparent',
                    color: t.buttonSecondaryText || t.text,
                    border: `1px solid ${t.buttonSecondaryBorder || t.borderStrong}`,
                    borderRadius: '6px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Poppins, sans-serif',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                >
                  View on Explorer <ExternalLink size={14} />
                </a>
              )}
              <button
                onClick={handleDone}
                style={{
                  background: t.buttonBg,
                  color: t.buttonText,
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '0.9';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Publish
              </button>
            </div>
          </>
        )}

        {/* ERROR — with diagnostics */}
        {status === 'error' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <AlertCircle size={28} color={t.error || '#dc2626'} />
              <h2 style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '22px',
                fontWeight: 600,
                color: t.text,
                margin: 0
              }}>
                Attestation Failed
              </h2>
            </div>

            <p style={{ fontSize: '14px', color: t.text, lineHeight: '1.6', marginBottom: '12px' }}>
              {errorMsg}
            </p>

            <div style={{
              background: t.surface,
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              border: `1px solid ${t.border || t.borderStrong}`
            }}>
              <p style={{ fontSize: '13px', color: t.textSecondary, lineHeight: '1.5', margin: 0 }}>
                {getErrorHelp()}
              </p>
            </div>

            <button
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                color: t.textSecondary,
                fontSize: '12px',
                fontFamily: 'Poppins, sans-serif',
                cursor: 'pointer',
                padding: '4px 0',
                marginBottom: showDiagnostics ? '8px' : '20px'
              }}
            >
              {showDiagnostics ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDiagnostics ? 'Hide' : 'Show'} diagnostics
            </button>

            {showDiagnostics && (
              <div style={{
                background: t.surface,
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
                border: `1px solid ${t.border || t.borderStrong}`,
                fontSize: '12px',
                fontFamily: 'monospace',
                color: t.textSecondary,
                lineHeight: '1.6'
              }}>
                <div>Error code: <span style={{ color: t.text }}>{errorCode}</span></div>
                <div>Wallet: <span style={{ color: t.text }}>{walletName || 'none'}</span></div>
                <div>Network: <span style={{ color: t.text }}>mainnet</span></div>
                <div>Balance: <span style={{ color: t.text }}>{balanceInfo ? balanceInfo.balanceAda.toFixed(2) + ' ADA' : 'unknown'}</span></div>
                <div>Required: <span style={{ color: t.text }}>{fees ? fees.minimumWalletBalance.ada + ' ADA' : 'unknown'}</span></div>
                <div>Time: <span style={{ color: t.text }}>{new Date().toISOString()}</span></div>
                <button
                  onClick={copyDiagnostics}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '8px',
                    background: 'none',
                    border: `1px solid ${t.borderStrong}`,
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontFamily: 'Poppins, sans-serif',
                    color: t.textSecondary,
                    cursor: 'pointer'
                  }}
                >
                  <Copy size={12} />
                  {copied ? 'Copied' : 'Copy diagnostics'}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={onCancel}
                style={{
                  background: t.buttonSecondaryBg || 'transparent',
                  color: t.buttonSecondaryText || t.text,
                  border: `1px solid ${t.buttonSecondaryBorder || t.borderStrong}`,
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s'
                }}
              >
                Back to Editor
              </button>
              <button
                onClick={() => { setStatus('idle'); setErrorMsg(''); setErrorCode(''); setShowDiagnostics(false); handleAttest(); }}
                style={{
                  background: t.buttonBg,
                  color: t.buttonText,
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '0.9';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Try Again
              </button>
            </div>
          </>
        )}
    </ModalShell>
  );
}